import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Pencil, Trash2, Wine } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { WineLotWithClient, useUpdateLot, useDeleteLot } from "@/hooks/useWineLots";
import { useVolumeUnit } from "@/hooks/useOrganization";
import { useVarietyColorMap, getVarietyColor } from "@/hooks/useVarietyColorMap";
import { useVarieties } from "@/hooks/useVarieties";
import { ClientSelect } from "@/components/shared/ClientSelect";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface LotDetailSheetProps {
  lot: WineLotWithClient | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function LotDetailSheet({ lot, open, onOpenChange }: LotDetailSheetProps) {
  const colorMap = useVarietyColorMap();
  const [isEditing, setIsEditing] = useState(false);
  const [code, setCode] = useState("");
  const [clientId, setClientId] = useState("");
  const [varietal, setVarietal] = useState("");
  const [vintage, setVintage] = useState("");
  const [volume, setVolume] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  const updateLot = useUpdateLot();
  const deleteLot = useDeleteLot();
  const { volumeLabel, volumeAbbrev } = useVolumeUnit();
  const { data: varieties = [] } = useVarieties();

  const handleEdit = () => {
    if (lot) {
      setCode(lot.code);
      setClientId(lot.client_id || "");
      setVarietal(lot.varietal || "");
      setVintage(lot.vintage?.toString() || "");
      setVolume(lot.current_volume_l?.toString() || "");
      setIsEditing(true);
    }
  };

  const handleSave = () => {
    if (lot) {
      updateLot.mutate(
        {
          id: lot.id,
          input: {
            code,
            client_id: clientId,
            varietal: varietal || undefined,
            vintage: vintage ? parseInt(vintage) : undefined,
            current_volume_l: volume ? parseFloat(volume) : undefined,
          },
        },
        {
          onSuccess: () => setIsEditing(false),
        }
      );
    }
  };

  const handleDelete = () => {
    if (lot) {
      deleteLot.mutate(lot.id, {
        onSuccess: () => {
          setShowDeleteDialog(false);
          onOpenChange(false);
        },
      });
    }
  };

  if (!lot) return null;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Lot Details</SheetTitle>
          </SheetHeader>

          <div className="space-y-6 py-6">
            {isEditing ? (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="edit-code">Lot Code</Label>
                  <Input
                    id="edit-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="edit-client">Client</Label>
                  <ClientSelect
                    value={clientId}
                    onValueChange={setClientId}
                  />
                </div>
                <div>
                  <Label htmlFor="edit-varietal">Varietal</Label>
                  <Select value={varietal} onValueChange={setVarietal}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select varietal" />
                    </SelectTrigger>
                    <SelectContent>
                      {varieties.map((v) => (
                        <SelectItem key={v.id} value={v.name}>
                          <div className="flex items-center gap-2">
                            {v.color && (
                              <span
                                className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: v.color }}
                              />
                            )}
                            {v.name} ({v.code})
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-vintage">Vintage</Label>
                    <Input
                      id="edit-vintage"
                      type="number"
                      value={vintage}
                      onChange={(e) => setVintage(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-volume">Current Volume ({volumeAbbrev})</Label>
                    <Input
                      id="edit-volume"
                      type="number"
                      value={volume}
                      onChange={(e) => setVolume(e.target.value)}
                      step="0.01"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleSave} disabled={updateLot.isPending}>
                    Save Changes
                  </Button>
                  <Button variant="outline" onClick={() => setIsEditing(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                      <Wine className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold">{lot.code}</h3>
                      {lot.client && (
                        <p className="text-sm text-muted-foreground">{lot.client.name}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {lot.varietal && (
                      <Badge
                        variant="secondary"
                        className="gap-1.5"
                        style={{
                          backgroundColor: getVarietyColor(colorMap, lot.varietal) + "20",
                          color: getVarietyColor(colorMap, lot.varietal),
                          borderColor: getVarietyColor(colorMap, lot.varietal) + "40",
                        }}
                      >
                        <span
                          className="inline-block h-2.5 w-2.5 rounded-full"
                          style={{ backgroundColor: getVarietyColor(colorMap, lot.varietal) }}
                        />
                        {lot.varietal}
                      </Badge>
                    )}
                    {lot.vintage && (
                      <Badge variant="outline">Vintage {lot.vintage}</Badge>
                    )}
                  </div>
                </div>

                <Separator />

                <div className="space-y-4">
                  <h4 className="font-semibold">Lot Information</h4>
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-sm text-muted-foreground">Current Volume</p>
                    <p className="text-2xl font-bold">
                      {lot.current_volume_l ? Math.round(lot.current_volume_l) : 0} {volumeAbbrev}
                    </p>
                  </div>
                </div>

                <Separator />

                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleEdit} className="flex-1">
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowDeleteDialog(true)}
                    className="flex-1 text-destructive hover:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </Button>
                </div>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Wine Lot</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete lot {lot.code}? This action cannot be undone and may affect associated work orders.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
